// ---------------------------------------------------------------------------
// FilterBar — Management View
//
// Visual pill bar for the Management View page (Scope / Window / Cadence).
// Pure presentation: state lives in `useManagementViewFilters` and is passed
// in as props plus setters. The page composes hook → bar.
//
// Phase 0 scope:
//   - Scope group renders only the active "All P&E" pill. The picker UI for
//     product / team scopes lands in a later phase. The hook still parses
//     forward-compat URL shapes (`?scope=product:<id>`, `?scope=team:<id>`).
//   - Window: 3m | 6m | 12m
//   - Cadence: Weekly | Monthly
//
// Visual reference: funnelin/Skystone_Management_View_Mockup.html `.fs` block.
// Mapped to existing semantic Tailwind tokens (no bespoke palette):
//   - active pill: bg-primary text-primary-foreground border-primary
//   - inactive  : bg-muted   text-foreground         border-border
// ---------------------------------------------------------------------------

import type {
  CadenceOption,
  ScopeOption,
  WindowOption,
} from '@skystone/shared';
import { cn } from '@/lib/utils';

const WINDOW_OPTIONS: ReadonlyArray<{ value: WindowOption; label: string }> = [
  { value: '3m', label: '3m' },
  { value: '6m', label: '6m' },
  { value: '12m', label: '12m' },
];

const CADENCE_OPTIONS: ReadonlyArray<{ value: CadenceOption; label: string }> = [
  { value: 'weekly', label: 'Weekly' },
  { value: 'monthly', label: 'Monthly' },
];

const PILL_BASE =
  'rounded px-2.5 py-1 text-xs font-semibold border transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring';
const PILL_ACTIVE = 'bg-primary text-primary-foreground border-primary';
const PILL_INACTIVE = 'bg-muted text-foreground border-border hover:bg-secondary';
// Disabled visual: dim + cursor-not-allowed. Native `disabled` on the button
// blocks click events, so the disabled appearance and the no-op behaviour are
// both delivered by the same attribute (PRODOPSAI-399).
const PILL_DISABLED = 'opacity-50 cursor-not-allowed hover:bg-muted';

const GROUP_LABEL =
  'text-[10px] font-bold uppercase tracking-wider text-muted-foreground';

const WINDOW_DISABLED_TOOLTIP = 'Window applies only when Cadence = Monthly';

function pillClass(active: boolean, disabled = false): string {
  return cn(PILL_BASE, active ? PILL_ACTIVE : PILL_INACTIVE, disabled && PILL_DISABLED);
}

/**
 * Phase 0 renders a single, always-active "All P&E" pill regardless of scope
 * kind — the picker UI lands later. The aria-label, however, surfaces any
 * forward-compat product/team id from the URL so screen-reader users can tell
 * the URL carries an unknown target the UI cannot yet act on.
 */
const SCOPE_PILL_LABEL = 'All P&E';

function scopeAriaLabel(scope: ScopeOption): string {
  if (scope.kind === 'product') {
    return `Scope: All P&E (forward-compat product id ${scope.productId} ignored in Phase 0)`;
  }
  if (scope.kind === 'team') {
    return `Scope: All P&E (forward-compat team id ${scope.teamId} ignored in Phase 0)`;
  }
  return 'Scope: All P&E';
}

interface FilterBarProps {
  scope: ScopeOption;
  window: WindowOption;
  cadence: CadenceOption;
  /** PRODOPSAI-620: include archived teams in all tab queries. Default off. */
  includeArchived: boolean;
  onWindowChange: (next: WindowOption) => void;
  onCadenceChange: (next: CadenceOption) => void;
  onIncludeArchivedChange: (next: boolean) => void;
}

export function FilterBar({
  scope,
  window,
  cadence,
  includeArchived,
  onWindowChange,
  onCadenceChange,
  onIncludeArchivedChange,
}: Readonly<FilterBarProps>) {
  return (
    <div
      className="flex flex-wrap items-center gap-x-3 gap-y-2 rounded-lg border border-border bg-background px-3.5 py-2.5 text-xs"
      data-testid="management-view-filter-bar"
    >
      {/* Scope group — Phase 0 renders the single "All P&E" pill */}
      <div className="flex items-center gap-2" role="radiogroup" aria-label="Scope">
        <span className={GROUP_LABEL}>Scope</span>
        <button
          type="button"
          role="radio"
          aria-checked
          aria-label={scopeAriaLabel(scope)}
          className={pillClass(true)}
          // Phase 0: Scope picker is not yet exposed; the pill is non-interactive
          // for switching but keeps the button semantics for a11y.
          disabled
        >
          {SCOPE_PILL_LABEL}
        </button>
      </div>

      {/* Window group — per source spec §3, Weekly cadence is fixed at 12 weeks
          regardless of Window. When Cadence=Weekly the Window pills are disabled
          (visually dimmed + native `disabled` blocks clicks) with a tooltip
          explaining the constraint. PRODOPSAI-399. */}
      <div
        className="flex items-center gap-2"
        role="radiogroup"
        aria-label="Window"
        aria-disabled={cadence === 'weekly' || undefined}
      >
        <span className={GROUP_LABEL}>Window</span>
        <div className="flex items-center gap-1">
          {WINDOW_OPTIONS.map((opt) => {
            const active = window === opt.value;
            const isDisabled = cadence === 'weekly';
            return (
              <button
                key={opt.value}
                type="button"
                role="radio"
                aria-checked={active}
                aria-disabled={isDisabled || undefined}
                disabled={isDisabled}
                title={isDisabled ? WINDOW_DISABLED_TOOLTIP : undefined}
                className={pillClass(active, isDisabled)}
                onClick={() => onWindowChange(opt.value)}
              >
                {opt.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Cadence group */}
      <div className="flex items-center gap-2" role="radiogroup" aria-label="Cadence">
        <span className={GROUP_LABEL}>Cadence</span>
        <div className="flex items-center gap-1">
          {CADENCE_OPTIONS.map((opt) => {
            const active = cadence === opt.value;
            return (
              <button
                key={opt.value}
                type="button"
                role="radio"
                aria-checked={active}
                className={pillClass(active)}
                onClick={() => onCadenceChange(opt.value)}
              >
                {opt.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* PRODOPSAI-620: opt-in inclusion of archived teams. Mirrors the
          AdminTeamsPage "Show archived" checkbox pattern; default off. */}
      <div className="flex items-center gap-2">
        <label
          htmlFor="mv-include-archived"
          className="text-xs text-muted-foreground cursor-pointer select-none"
        >
          Include archived teams
        </label>
        <input
          id="mv-include-archived"
          type="checkbox"
          checked={includeArchived}
          onChange={(e) => onIncludeArchivedChange(e.target.checked)}
          className="h-4 w-4 rounded border-input accent-primary cursor-pointer"
          data-testid="management-view-include-archived"
        />
      </div>
    </div>
  );
}
